// @ts-strict-ignore
import { ButtonProps } from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';
import { RunwayUserAction } from '../helpers/aclHelpers';
import {
    integrationTypeShouldDisableForAppStoreType,
    integrationTypeShouldDisableForPlatform,
    oauthInstallationUrlForIntegrationType,
    providerTypeForIntegrationType,
} from '../helpers/integrationHelpers';
import { AppPlatform, AppStoreType, IntegrationProvider, IntegrationType } from '../types';
import { SetupRouteComponentMatchParams } from '../typescriptHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import routes from '../routes';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAppHasIntegrationConnected,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsWorkflowsMigratedAndEnabled,
} from '@/selectors/selectors';

interface ConnectButtonForIntegrationTypeProps extends ButtonProps {
    integrationType: IntegrationType;
    appPlatform: AppPlatform;
    appStoreType: AppStoreType;
    orgId: string;
    orgIdParam: string;
    appIdParam: string;
    integrationProviders: IntegrationProvider[];
    onClickShowInstallationModal?: (integrationType: IntegrationType) => void;
    onClickShowProviderModal?: (integrationType: IntegrationType) => void;
    onClickInstallRunwayIntegration?: (integrationType: IntegrationType) => void;
    canInstallMultipleIntegrationsForCapability?: boolean;
    customDisabledTooltip?: string;
}

export const ConnectButtonForIntegrationType = (props: ConnectButtonForIntegrationTypeProps) => {
    const {
        integrationType,
        appPlatform,
        appStoreType,
        orgId,
        orgIdParam,
        appIdParam,
        integrationProviders,
        disabled,
        onClickShowInstallationModal,
        onClickShowProviderModal,
        onClickInstallRunwayIntegration,
        customDisabledTooltip,
        ...rest
    } = props;
    const providerForIntegrationType = integrationProviders.find((provider) => {
        if (provider.type === providerTypeForIntegrationType(integrationType)) {
            return provider;
        }
        return null;
    });

    // canInstallMultipleIntegrationsForCapability will be true for apps
    // where we've enabled a feature flag to allow multiple integrations for a single capability,
    // OR for OBAA integrations (which are always allowed to have multiple integrations for a capability).
    //
    // if props.canInstallMultipleIntegrationsForCapability is true, we're most likely dealing with a workflows app,
    // which is the only case where we allow multiple integrations for a capability outside of OBAA.
    // We therefore don't want to disable an integration based on the platform or app store type, since
    // the app-level platform and AST aren't relevant for workflows as the app/workflow could have multiple platforms or ASTs.
    //
    // possible issue:
    // canInstallMultipleIntegrationsForCapability is always true for OBAA integrations, so this means that
    // we wouldn't be able to disable an OBAA integration based on platform or app store type.
    //
    // this probably needs some work if we ever want to disable an OBAA, or any other integration in a WF app.

    //Alex: Leaving the previous comment here for reference, now instead of checking the values for the app, we check the values for the destinations array.

    const isWorkflowsMigratedAndEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsMigratedAndEnabled(state, appIdParam),
    );

    let integrationTypeDisabledForPlatform = false;
    let integrationTypeDisabledForAppStoreType = false;

    if (isWorkflowsMigratedAndEnabled) {
        const destinations = useSelector((state: RootState) =>
            Object.values(selectDestinationInfoByDestinationStepConfigId(state, appIdParam)),
        );
        integrationTypeDisabledForPlatform = destinations.every((destination) =>
            integrationTypeShouldDisableForPlatform(integrationType, destination.platform),
        );
        integrationTypeDisabledForAppStoreType = destinations.every((destination) =>
            integrationTypeShouldDisableForAppStoreType(
                integrationType,
                destination.platform,
                destination.appStoreType,
            ),
        );
    } else {
        integrationTypeDisabledForPlatform = integrationTypeShouldDisableForPlatform(integrationType, appPlatform);
        integrationTypeDisabledForAppStoreType = integrationTypeShouldDisableForAppStoreType(
            integrationType,
            appPlatform,
            appStoreType,
        );
    }

    let integrationTypeDisabled =
        !props.canInstallMultipleIntegrationsForCapability &&
        (integrationTypeDisabledForPlatform || integrationTypeDisabledForAppStoreType);

    // based on the above comment, a hacky way to disable AndroidVitals OBAA integration for non-android apps
    // TODO-WF how can we do this better? We need to disable certain integrations based on the platform or AST,
    // but in a WF app, we can have multiple platforms or ASTs
    if (
        integrationType === IntegrationType.AndroidVitals ||
        integrationType === IntegrationType.ApplePowerPerformanceMetrics
    ) {
        integrationTypeDisabled = integrationTypeDisabledForPlatform || integrationTypeDisabledForAppStoreType;
    }

    // Check if JiraServiceManagementScheduling requires JiraIssueTracking integration
    const hasJiraIssueTracking = useSelector((state: RootState) =>
        selectAppHasIntegrationConnected(state, appIdParam, IntegrationType.Jira),
    );

    const isJSMDisabled = integrationType === IntegrationType.JiraServiceManagementScheduling && !hasJiraIssueTracking;
    const jsmDisabledTooltip =
        customDisabledTooltip ||
        'Jira Service Management requires a Jira integration to be connected first from the Project Management integrations section';

    if (
        providerForIntegrationType &&
        integrationType !== IntegrationType.CustomAnalytics &&
        integrationType !== IntegrationType.CustomCI
    ) {
        // at least one provider exists, show provider modal
        const buttonDisabled = integrationTypeDisabled || disabled || isJSMDisabled;
        const button = (
            <PermissionedActionButton
                actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                onClick={() => onClickShowProviderModal(integrationType)}
                disabled={buttonDisabled}
                {...rest}
            >
                Connect
            </PermissionedActionButton>
        );

        if (isJSMDisabled) {
            return (
                <Tooltip title={jsmDisabledTooltip} placement="top">
                    {button}
                </Tooltip>
            );
        }

        return button;
    } else {
        const buttonDisabled = integrationTypeDisabled || disabled || isJSMDisabled;
        const button = (
            <ConnectNewProviderButtonForIntegrationType
                cta="Connect"
                integrationType={integrationType}
                orgId={orgId}
                orgIdParam={orgIdParam}
                appIdParam={appIdParam}
                disabled={buttonDisabled}
                onClickShowInstallationModal={onClickShowInstallationModal}
                onClickInstallRunwayIntegration={onClickInstallRunwayIntegration}
                {...rest}
            />
        );

        if (isJSMDisabled) {
            return (
                <Tooltip title={jsmDisabledTooltip} placement="top">
                    {button}
                </Tooltip>
            );
        }

        return button;
    }
};

interface ConnectNewProviderButtonForIntegrationTypeProps extends ButtonProps {
    cta: string;
    integrationType: IntegrationType;
    orgId: string;
    orgIdParam: string;
    appIdParam: string;
    onClickShowInstallationModal?: (integrationType: IntegrationType) => void;
    onClickInstallRunwayIntegration?: (integrationType: IntegrationType) => void;
}
export const ConnectNewProviderButtonForIntegrationType = (props: ConnectNewProviderButtonForIntegrationTypeProps) => {
    const { setupTypeParam } = useParams<SetupRouteComponentMatchParams>();
    const isSetupRoute = !!setupTypeParam;
    const automationsToolboxRouteMatch = useRouteMatch(routes.automationsToolboxIntegrations);
    const isAutomationsRoute = !!automationsToolboxRouteMatch;

    const {
        cta,
        integrationType,
        orgId,
        orgIdParam,
        appIdParam,
        onClickShowInstallationModal,
        onClickInstallRunwayIntegration,
        ...rest
    } = props;

    const redirectURL = oauthInstallationUrlForIntegrationType(
        integrationType,
        orgId,
        orgIdParam,
        appIdParam,
        isSetupRoute || isAutomationsRoute, // if it's setup or automations route, we want to redirect to same page this is initiated from
    );
    switch (integrationType) {
        case IntegrationType.GitLabVCS:
        case IntegrationType.GitLabCI:
        case IntegrationType.Slack:
        case IntegrationType.Bitbucket:
        case IntegrationType.BitbucketCI:
        case IntegrationType.GitHubVCS:
        case IntegrationType.GitHubCI:
        case IntegrationType.GitHubIT:
        case IntegrationType.GitHubVCSDistribution:
        case IntegrationType.GitHubCIDistribution:
        case IntegrationType.Linear:
        case IntegrationType.Jira:
        case IntegrationType.JiraServiceManagementScheduling:
        case IntegrationType.Asana:
        case IntegrationType.AzureCI:
        case IntegrationType.AzureIT:
        case IntegrationType.AzureVCS:
        case IntegrationType.Monday:
        case IntegrationType.MicrosoftTeams:
        case IntegrationType.GooglePersonalCalendar:
            return (
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                    href={redirectURL}
                    {...rest}
                >
                    {cta}
                </PermissionedActionButton>
            );
        case IntegrationType.Jenkins:
        case IntegrationType.CircleCI:
        case IntegrationType.Codemagic:
        case IntegrationType.Bitrise:
        case IntegrationType.Buildkite:
        case IntegrationType.Bugsnag:
        case IntegrationType.Sentry:
        case IntegrationType.TestFlight:
        case IntegrationType.Pivotal:
        case IntegrationType.Shortcut:
        case IntegrationType.Apple:
        case IntegrationType.Google:
        case IntegrationType.Amazon:
        case IntegrationType.TestRail:
        case IntegrationType.BrowserStack:
        case IntegrationType.Qase:
        case IntegrationType.GoogleBeta:
        case IntegrationType.TravisCI:
        case IntegrationType.Firebase:
        case IntegrationType.Xray:
        case IntegrationType.AppCenterCI:
        case IntegrationType.Amplitude:
        case IntegrationType.Optimizely:
        case IntegrationType.LaunchDarkly:
        case IntegrationType.Statsig:
        case IntegrationType.DatadogAnalytics:
        case IntegrationType.AppCenterBeta:
        case IntegrationType.GenericCI:
        case IntegrationType.CustomCI:
        case IntegrationType.MixpanelAnalytics:
        case IntegrationType.XcodeCloud:
        case IntegrationType.FirebaseBeta:
        case IntegrationType.EmbraceStabilityMonitoring:
        case IntegrationType.GoogleAnalytics:
        case IntegrationType.AndroidVitals:
        case IntegrationType.ApplePowerPerformanceMetrics:
        case IntegrationType.DatadogStabilityMonitoring:
        case IntegrationType.Crowdin:
        case IntegrationType.PagerDutyScheduling:
        case IntegrationType.NewRelicStabilityMonitoring:
        case IntegrationType.NewRelicAnalytics:
        case IntegrationType.Lokalise:
        case IntegrationType.HuaweiAppGallery:
        case IntegrationType.Samsung:
        case IntegrationType.DynatraceStabilityMonitoring:
        case IntegrationType.OpsgenieScheduling:
        case IntegrationType.CustomAnalytics:
        case IntegrationType.GoogleCalendar:
            return (
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                    onClick={() => onClickShowInstallationModal(integrationType)}
                    {...rest}
                >
                    {cta}
                </PermissionedActionButton>
            );
        case IntegrationType.RunwayBuildDistroBeta:
            return (
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                    onClick={() => onClickInstallRunwayIntegration && onClickInstallRunwayIntegration(integrationType)}
                    {...rest}
                >
                    {cta}
                </PermissionedActionButton>
            );
        default:
            return null;
    }
};
